import React from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Euro,
  FileText,
  Info,
  Landmark,
  LoaderCircle,
  Percent,
  ReceiptText,
  ShieldCheck,
  WalletCards,
  X,
} from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./LenderAdditionalLoanPage.css";
import { useToast } from "../../../components/feedback/ToastProvider";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const EMPTY_FORM = {
  additional_renovation_loan_amount: "",
  tenure_years: "",
  indicative_apr_pct: "",
  estimated_monthly_payment: "",
  loan_start_date: "",
  monthly_interest_rate_pct: "",
  mortgage_amount: "",
  mortgage_mandate_amount: "",
  lender_reference: "",
  reason: "",
  conditions: "",
  notes: "",
};

function parseNumber(value) {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

function sanitizeNumber(value, decimals = 2) {
  const raw = String(value ?? "")
    .replace(/\s/g, "")
    .replace(/[^\d.,]/g, "");

  if (!raw) {
    return "";
  }

  // Whole-number fields such as tenure.
  if (decimals === 0) {
    return raw.replace(/[^\d]/g, "");
  }

  const hasComma = raw.includes(",");
  const hasDot = raw.includes(".");

  let integerPart = "";
  let decimalPart = "";
  let hasDecimalSeparator = false;

  // Both comma and dot:
  // right-most one is decimal separator.
  if (hasComma && hasDot) {
    const lastComma = raw.lastIndexOf(",");
    const lastDot = raw.lastIndexOf(".");
    const decimalIndex = Math.max(lastComma, lastDot);

    integerPart = raw.slice(0, decimalIndex).replace(/[^\d]/g, "");

    decimalPart = raw
      .slice(decimalIndex + 1)
      .replace(/[^\d]/g, "")
      .slice(0, decimals);

    hasDecimalSeparator = true;
  } else if (hasComma) {
    const lastComma = raw.lastIndexOf(",");

    const digitsAfterComma = raw.slice(lastComma + 1).replace(/[^\d]/g, "");

    const looksLikeThousandsGrouping = digitsAfterComma.length >= 3;

    if (looksLikeThousandsGrouping) {
      integerPart = raw.replace(/[^\d]/g, "");
    } else {
      integerPart = raw.slice(0, lastComma).replace(/[^\d]/g, "");

      decimalPart = digitsAfterComma.slice(0, decimals);

      hasDecimalSeparator = true;
    }
  } else if (hasDot) {
    const lastDot = raw.lastIndexOf(".");

    integerPart = raw.slice(0, lastDot).replace(/[^\d]/g, "");

    decimalPart = raw
      .slice(lastDot + 1)
      .replace(/[^\d]/g, "")
      .slice(0, decimals);

    hasDecimalSeparator = true;
  } else {
    integerPart = raw.replace(/[^\d]/g, "");
  }

  if (!integerPart && hasDecimalSeparator) {
    integerPart = "0";
  }

  if (!hasDecimalSeparator) {
    return integerPart;
  }

  if (raw.endsWith(".") || raw.endsWith(",")) {
    return `${integerPart}.`;
  }

  return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
}

function formatNumberInput(value, decimals = 2) {
  if (value === null || value === undefined || value === "") return "";

  const text = String(value);
  const normalized = text.replace(",", ".");
  const numeric = Number(normalized);

  if (!Number.isFinite(numeric)) return "";

  if (text.endsWith(".") || text.endsWith(",")) {
    return `${Math.trunc(numeric).toLocaleString("en-US")}.`;
  }

  const [integerPart, decimalPart] = normalized.split(".");
  const formatted = Number(integerPart || 0).toLocaleString("en-US");

  if (decimalPart === undefined) return formatted;
  return `${formatted}.${decimalPart.slice(0, decimals)}`;
}

function formatEuro(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numeric);
}

function calculateMonthlyPayment(principal, annualApr, tenureYears) {
  const amount = parseNumber(principal);
  const apr = parseNumber(annualApr);
  const years = parseNumber(tenureYears);

  if (amount <= 0 || apr < 0 || years <= 0) return null;

  const n = years * 12;
  const r = apr / 100 / 12;

  if (r === 0) return amount / n;

  const factor = Math.pow(1 + r, n);
  return amount * ((r * factor) / (factor - 1));
}

function extractApiError(data, fallback) {
  if (!data || typeof data !== "object") return fallback;

  const containers = [data.errors, data];

  for (const container of containers) {
    if (!container || typeof container !== "object") continue;

    for (const [field, value] of Object.entries(container)) {
      if (["detail", "message", "errors"].includes(field)) continue;

      if (Array.isArray(value) && value.length) {
        return `${field.replaceAll("_", " ")}: ${value[0]}`;
      }

      if (typeof value === "string" && value.trim()) {
        return `${field.replaceAll("_", " ")}: ${value}`;
      }
    }
  }

  return data.detail || data.message || fallback;
}

function FormField({
  label,
  icon,
  value,
  onChange,
  placeholder,
  inputMode = "text",
  type = "text",
  helper = "",
  error = "",
  readOnly = false,
}) {
  return (
    <label
      className={`additional-loan-field ${
        error ? "additional-loan-field--error" : ""
      }`}
    >
      <span className="additional-loan-field__label">
        {icon}
        {label}
      </span>

      <input
        className="additional-loan-field__input"
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
      />

      {error ? (
        <span className="additional-loan-field__message">
          <AlertTriangle size={13} />
          {error}
        </span>
      ) : helper ? (
        <span className="additional-loan-field__helper">{helper}</span>
      ) : null}
    </label>
  );
}

function TextareaField({ label, value, onChange, placeholder, helper = "" }) {
  return (
    <label className="additional-loan-field">
      <span className="additional-loan-field__label">
        <FileText size={15} />
        {label}
      </span>

      <textarea
        className="additional-loan-field__textarea"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />

      {helper ? (
        <span className="additional-loan-field__helper">{helper}</span>
      ) : null}
    </label>
  );
}

export default function LenderAdditionalLoanPage() {
  const { t } = useTranslation("lender-additional-loan");
  const { loanCaseId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { setFormDirty, requestNavigation } = useOutletContext() || {};
  const { showToast } = useToast();

  const [context, setContext] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [errors, setErrors] = React.useState({});
  const [loadError, setLoadError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);

  React.useEffect(() => {
    setFormDirty?.(isDirty);
    return () => setFormDirty?.(false);
  }, [isDirty, setFormDirty]);

  React.useEffect(() => {
    if (!isAuthenticated || !loanCaseId) {
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();

    async function loadContext() {
      try {
        setIsLoading(true);
        setLoadError("");

        const token = await getAccessTokenSilently();

        const response = await fetch(
          `${API_BASE}/api/lender/loan-cases/${loanCaseId}/additional-loan/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            extractApiError(
              data,
              t(
                "errors.loadFailed",
                "The additional-loan context could not be loaded.",
              ),
            ),
          );
        }

        setContext(data);

        const defaults = data?.defaults || {};

        setForm({
          ...EMPTY_FORM,
          tenure_years:
            defaults.tenure_years !== null &&
            defaults.tenure_years !== undefined
              ? String(defaults.tenure_years)
              : "",
          indicative_apr_pct:
            defaults.indicative_apr_pct !== null &&
            defaults.indicative_apr_pct !== undefined
              ? String(defaults.indicative_apr_pct)
              : "",
          monthly_interest_rate_pct:
            defaults.monthly_interest_rate_pct !== null &&
            defaults.monthly_interest_rate_pct !== undefined
              ? String(defaults.monthly_interest_rate_pct)
              : "",
          loan_start_date: defaults.loan_start_date || "",
        });

        setIsDirty(false);
      } catch (error) {
        if (error?.name === "AbortError") return;
        setLoadError(
          error?.message ||
            t(
              "errors.loadFailed",
              "The additional-loan context could not be loaded.",
            ),
        );
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }

    loadContext();

    return () => controller.abort();
  }, [getAccessTokenSilently, isAuthenticated, loanCaseId, t]);

  const amount = parseNumber(form.additional_renovation_loan_amount);
  const existingPotentialFunding = parseNumber(
    context?.financing?.potential_renovation_funding,
  );
  const invoicesIssued = parseNumber(context?.invoices?.issued_amount);
  const combinedPotentialFunding = existingPotentialFunding + amount;
  const postLoanFundingRemaining = Math.max(
    combinedPotentialFunding - invoicesIssued,
    0,
  );

  function updateField(field, value) {
    setForm((previous) => {
      const next = {
        ...previous,
        [field]: value,
      };

      if (
        [
          "additional_renovation_loan_amount",
          "tenure_years",
          "indicative_apr_pct",
        ].includes(field)
      ) {
        const monthly = calculateMonthlyPayment(
          field === "additional_renovation_loan_amount"
            ? value
            : next.additional_renovation_loan_amount,
          field === "indicative_apr_pct" ? value : next.indicative_apr_pct,
          field === "tenure_years" ? value : next.tenure_years,
        );

        next.estimated_monthly_payment =
          monthly === null ? "" : monthly.toFixed(2);
      }

      return next;
    });

    setErrors((previous) => ({
      ...previous,
      [field]: "",
    }));

    setIsDirty(true);
  }

  function validate() {
    const nextErrors = {};

    if (amount <= 0) {
      nextErrors.additional_renovation_loan_amount = t(
        "validation.amount",
        "Enter an additional loan amount above zero.",
      );
    }

    const tenure = parseNumber(form.tenure_years);
    if (tenure <= 0 || !Number.isInteger(tenure)) {
      nextErrors.tenure_years = t(
        "validation.tenure",
        "Enter a positive whole number of years.",
      );
    }

    if (parseNumber(form.indicative_apr_pct) <= 0) {
      nextErrors.indicative_apr_pct = t(
        "validation.apr",
        "Enter an APR above zero.",
      );
    }

    if (parseNumber(form.estimated_monthly_payment) <= 0) {
      nextErrors.estimated_monthly_payment = t(
        "validation.monthlyPayment",
        "Enter a monthly payment above zero.",
      );
    }

    if (!form.loan_start_date) {
      nextErrors.loan_start_date = t(
        "validation.startDate",
        "Select the loan start date.",
      );
    }

    if (parseNumber(form.monthly_interest_rate_pct) <= 0) {
      nextErrors.monthly_interest_rate_pct = t(
        "validation.monthlyRate",
        "Enter the monthly interest rate.",
      );
    }

    const mortgage = parseNumber(form.mortgage_amount);
    const mandate = parseNumber(form.mortgage_mandate_amount);

    if (
      (form.mortgage_amount || form.mortgage_mandate_amount) &&
      Math.abs(mortgage + mandate - amount) > 0.01
    ) {
      const message = t(
        "validation.securitySplit",
        "Mortgage amount and mortgage mandate must together equal the additional loan amount.",
      );

      nextErrors.mortgage_amount = message;
      nextErrors.mortgage_mandate_amount = message;
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      showToast({
        type: "error",
        title: t("toast.validationTitle", "Check the additional loan"),
        message: Object.values(nextErrors)[0],
      });
      return false;
    }

    return true;
  }

  function handleBack() {
    const target = "/lender/loan-cases";

    if (requestNavigation) {
      requestNavigation(target);
      return;
    }

    navigate(target);
  }

  async function submitAdditionalLoan() {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const token = await getAccessTokenSilently();

      const payload = {
        additional_renovation_loan_amount:
          form.additional_renovation_loan_amount,
        tenure_years: form.tenure_years,
        indicative_apr_pct: form.indicative_apr_pct,
        estimated_monthly_payment: form.estimated_monthly_payment,
        loan_start_date: form.loan_start_date,
        monthly_interest_rate_pct: form.monthly_interest_rate_pct,
        mortgage_amount: form.mortgage_amount || null,
        mortgage_mandate_amount: form.mortgage_mandate_amount || null,
        lender_reference: form.lender_reference.trim(),
        reason: form.reason.trim(),
        conditions: form.conditions.trim(),
        notes: form.notes.trim(),
      };

      const response = await fetch(
        `${API_BASE}/api/lender/loan-cases/${loanCaseId}/additional-loan/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          extractApiError(
            data,
            t(
              "errors.createFailed",
              "The additional renovation loan could not be created.",
            ),
          ),
        );
      }

      setIsDirty(false);
      setFormDirty?.(false);
      setShowConfirm(false);

      showToast({
        type: "success",
        title: t("toast.createdTitle", "Additional loan recorded"),
        message: t(
          "toast.createdText",
          "The additional renovation loan was added to the existing renovation project.",
        ),
      });

      navigate("/lender/loan-cases", {
        replace: true,
        state: {
          additionalLoanCreated: true,
          loanCaseId: data?.loan_case?.id,
          parentLoanCaseId: Number(loanCaseId),
        },
      });
    } catch (error) {
      showToast({
        type: "error",
        title: t("toast.createFailedTitle", "Could not add loan"),
        message:
          error?.message ||
          t(
            "errors.createFailed",
            "The additional renovation loan could not be created.",
          ),
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="additional-loan-page__state">
        <LoaderCircle className="additional-loan-page__spinner" size={28} />
        <p>{t("loading", "Loading additional-loan context...")}</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="additional-loan-page__state additional-loan-page__state--error">
        <AlertTriangle size={25} />
        <h2>{t("errors.title", "Additional loan unavailable")}</h2>
        <p>{loadError}</p>
        <button type="button" onClick={handleBack}>
          <ArrowLeft size={15} />
          {t("actions.back", "Back to loan cases")}
        </button>
      </div>
    );
  }

  return (
    <div className="additional-loan-page">
      <div className="additional-loan-page__breadcrumb">
        <button type="button" onClick={handleBack}>
          <ArrowLeft size={15} />
          {t("actions.back", "Loan cases")}
        </button>
        <span>/</span>
        <strong>
          {context?.parent_loan_case?.case_reference || `#${loanCaseId}`}
        </strong>
      </div>

      <div className="additional-loan-page__hero">
        <div>
          <span className="additional-loan-page__eyebrow">
            {t("eyebrow", "Additional renovation financing")}
          </span>
          <h1>{t("title", "Add renovation loan")}</h1>
          <p>
            {t(
              "subtitle",
              "Record a separate lender loan when the homebuyer needs additional financing for the existing renovation project.",
            )}
          </p>
        </div>

        <div className="additional-loan-page__hero-badge">
          <ShieldCheck size={16} />
          {t("linkedCase", "Linked to existing loan")}
        </div>
      </div>

      <div className="additional-loan-page__notice">
        <Info size={18} />
        <div>
          <strong>
            {t("notice.title", "This creates a separate additional loan case")}
          </strong>
          <p>
            {t(
              "notice.text",
              "The original mortgage and purchase financing remain unchanged. The new loan is renovation-only and is linked to the same dossier and renovation project.",
            )}
          </p>
        </div>
      </div>

      <div className="additional-loan-page__layout">
        <main className="additional-loan-page__main">
          <section className="additional-loan-card">
            <div className="additional-loan-card__header">
              <div>
                <span className="additional-loan-card__kicker">
                  {t("context.kicker", "Current financing context")}
                </span>
                <h2>{context?.homebuyer?.name || "Homebuyer"}</h2>
                <p>
                  {context?.project?.code
                    ? `${t("context.project", "Project")} ${context.project.code}`
                    : t(
                        "context.projectFallback",
                        "Existing renovation project",
                      )}
                </p>
              </div>

              <span className="additional-loan-card__status">
                {t("context.approved", "Initial loan approved")}
              </span>
            </div>

            <div className="additional-loan-summary-grid">
              <div className="additional-loan-summary-item">
                <span>
                  {t(
                    "summary.initialRenovationLoan",
                    "Initial lender renovation loan",
                  )}
                </span>
                <strong>
                  {formatEuro(
                    Number(
                      context?.financing?.initial_lender_renovation_loan || 0,
                    ),
                  )}
                </strong>
              </div>

              <div className="additional-loan-summary-item">
                <span>
                  {t("summary.otherFunding", "Other renovation funding")}
                </span>
                <strong>
                  {formatEuro(
                    Number(context?.financing?.other_renovation_funding || 0),
                  )}
                </strong>
              </div>

              <div className="additional-loan-summary-item additional-loan-summary-item--highlight">
                <span>
                  {t(
                    "summary.currentPotential",
                    "Current potential renovation funding",
                  )}
                </span>
                <strong>{formatEuro(existingPotentialFunding)}</strong>
              </div>

              <div className="additional-loan-summary-item">
                <span>{t("summary.invoices", "Invoices issued")}</span>
                <strong>{formatEuro(invoicesIssued)}</strong>
              </div>

              <div className="additional-loan-summary-item">
                <span>
                  {t("summary.allocated", "Funding allocated to invoices")}
                </span>
                <strong>
                  {formatEuro(Number(context?.invoices?.allocated_amount || 0))}
                </strong>
              </div>

              <div className="additional-loan-summary-item">
                <span>
                  {t("summary.unallocated", "Unallocated invoice amount")}
                </span>
                <strong>
                  {formatEuro(
                    Number(context?.invoices?.unallocated_amount || 0),
                  )}
                </strong>
              </div>
            </div>
          </section>

          <section className="additional-loan-card">
            <div className="additional-loan-card__header">
              <div>
                <span className="additional-loan-card__kicker">
                  {t("form.kicker", "New loan")}
                </span>
                <h2>{t("form.title", "Additional renovation loan terms")}</h2>
                <p>
                  {t(
                    "form.subtitle",
                    "Only the additional renovation financing is recorded in this loan case.",
                  )}
                </p>
              </div>
            </div>

            <div className="additional-loan-form-grid">
              <FormField
                label={t("fields.amount", "Additional renovation loan (€)")}
                icon={<Euro size={15} />}
                value={formatNumberInput(
                  form.additional_renovation_loan_amount,
                )}
                onChange={(value) =>
                  updateField(
                    "additional_renovation_loan_amount",
                    sanitizeNumber(value),
                  )
                }
                inputMode="decimal"
                placeholder="15,000"
                error={errors.additional_renovation_loan_amount}
                helper={t(
                  "fields.amountHelp",
                  "This amount will become a separate lender funding source for the renovation project.",
                )}
              />

              <FormField
                label={t("fields.tenure", "Financing term (years)")}
                icon={<Clock3 size={15} />}
                value={formatNumberInput(form.tenure_years, 0)}
                onChange={(value) =>
                  updateField("tenure_years", sanitizeNumber(value, 0))
                }
                inputMode="numeric"
                placeholder="10"
                error={errors.tenure_years}
              />

              <FormField
                label={t("fields.apr", "Indicative APR (%)")}
                icon={<Percent size={15} />}
                value={formatNumberInput(form.indicative_apr_pct, 4)}
                onChange={(value) =>
                  updateField("indicative_apr_pct", sanitizeNumber(value, 4))
                }
                inputMode="decimal"
                placeholder="3.60"
                error={errors.indicative_apr_pct}
              />

              <FormField
                label={t(
                  "fields.monthlyPayment",
                  "Estimated monthly payment (€)",
                )}
                icon={<WalletCards size={15} />}
                value={formatNumberInput(form.estimated_monthly_payment)}
                onChange={(value) =>
                  updateField(
                    "estimated_monthly_payment",
                    sanitizeNumber(value),
                  )
                }
                inputMode="decimal"
                placeholder="150.00"
                error={errors.estimated_monthly_payment}
                helper={t(
                  "fields.monthlyPaymentHelp",
                  "Calculated automatically from amount, APR and term; you can adjust it if required.",
                )}
              />

              <FormField
                label={t("fields.startDate", "Loan start date")}
                icon={<CalendarDays size={15} />}
                value={form.loan_start_date}
                onChange={(value) => updateField("loan_start_date", value)}
                type="date"
                error={errors.loan_start_date}
              />

              <FormField
                label={t("fields.monthlyRate", "Monthly interest rate (%)")}
                icon={<Percent size={15} />}
                value={formatNumberInput(form.monthly_interest_rate_pct, 5)}
                onChange={(value) =>
                  updateField(
                    "monthly_interest_rate_pct",
                    sanitizeNumber(value, 5),
                  )
                }
                inputMode="decimal"
                placeholder="0.30000"
                error={errors.monthly_interest_rate_pct}
              />
            </div>
          </section>

          <section className="additional-loan-card">
            <div className="additional-loan-card__header">
              <div>
                <span className="additional-loan-card__kicker">
                  {t("security.kicker", "Security and references")}
                </span>
                <h2>{t("security.title", "Additional loan details")}</h2>
                <p>
                  {t(
                    "security.subtitle",
                    "Security fields are optional, but when used their combined value must equal the additional loan amount.",
                  )}
                </p>
              </div>
            </div>

            <div className="additional-loan-form-grid">
              <FormField
                label={t("fields.mortgage", "Mortgage amount (€)")}
                icon={<Landmark size={15} />}
                value={formatNumberInput(form.mortgage_amount)}
                onChange={(value) =>
                  updateField("mortgage_amount", sanitizeNumber(value))
                }
                inputMode="decimal"
                placeholder="0"
                error={errors.mortgage_amount}
              />

              <FormField
                label={t("fields.mandate", "Mortgage mandate amount (€)")}
                icon={<Landmark size={15} />}
                value={formatNumberInput(form.mortgage_mandate_amount)}
                onChange={(value) =>
                  updateField("mortgage_mandate_amount", sanitizeNumber(value))
                }
                inputMode="decimal"
                placeholder="0"
                error={errors.mortgage_mandate_amount}
              />

              <FormField
                label={t("fields.reference", "Lender reference")}
                icon={<FileText size={15} />}
                value={form.lender_reference}
                onChange={(value) => updateField("lender_reference", value)}
                placeholder="Optional internal loan reference"
              />
            </div>

            <div className="additional-loan-textareas">
              <TextareaField
                label={t("fields.reason", "Reason for additional financing")}
                value={form.reason}
                onChange={(value) => updateField("reason", value)}
                placeholder={t(
                  "fields.reasonPlaceholder",
                  "For example: revised renovation plan, higher actual works cost, or additional eligible works.",
                )}
              />

              <TextareaField
                label={t("fields.conditions", "Conditions")}
                value={form.conditions}
                onChange={(value) => updateField("conditions", value)}
                placeholder={t(
                  "fields.conditionsPlaceholder",
                  "Optional conditions attached to the additional loan.",
                )}
              />

              <TextareaField
                label={t("fields.notes", "Internal lender notes")}
                value={form.notes}
                onChange={(value) => updateField("notes", value)}
                placeholder={t(
                  "fields.notesPlaceholder",
                  "Optional internal notes. These are not used as the customer-facing reason.",
                )}
              />
            </div>
          </section>
        </main>

        <aside className="additional-loan-page__aside">
          <section className="additional-loan-preview">
            <div className="additional-loan-preview__header">
              <Building2 size={18} />
              <div>
                <span>{t("preview.kicker", "After this loan")}</span>
                <strong>
                  {t("preview.title", "Renovation financing preview")}
                </strong>
              </div>
            </div>

            <div className="additional-loan-preview__rows">
              <div>
                <span>
                  {t("preview.currentFunding", "Current potential funding")}
                </span>
                <strong>{formatEuro(existingPotentialFunding)}</strong>
              </div>

              <div className="additional-loan-preview__plus">
                <span>
                  {t("preview.additionalLoan", "Additional lender loan")}
                </span>
                <strong>
                  {amount > 0 ? `+${formatEuro(amount)}` : formatEuro(0)}
                </strong>
              </div>

              <div className="additional-loan-preview__total">
                <span>
                  {t(
                    "preview.newPotential",
                    "New potential renovation funding",
                  )}
                </span>
                <strong>{formatEuro(combinedPotentialFunding)}</strong>
              </div>

              <div>
                <span>{t("preview.invoicesIssued", "Invoices issued")}</span>
                <strong>{formatEuro(invoicesIssued)}</strong>
              </div>

              <div>
                <span>
                  {t(
                    "preview.remaining",
                    "Funding remaining after current invoices",
                  )}
                </span>
                <strong>{formatEuro(postLoanFundingRemaining)}</strong>
              </div>
            </div>

            <div className="additional-loan-preview__note">
              <ReceiptText size={15} />
              <p>
                {t(
                  "preview.note",
                  "Creating this loan does not allocate it to invoices automatically. It becomes a new eligible lender funding source and can then be allocated from the Renovation Invoices page.",
                )}
              </p>
            </div>
          </section>
        </aside>
      </div>

      <div className="additional-loan-actions">
        <div>
          <strong>{t("actions.title", "Additional renovation loan")}</strong>
          <span>
            {isDirty
              ? t("actions.unsaved", "You have unsaved changes.")
              : t(
                  "actions.ready",
                  "Enter the additional loan terms to continue.",
                )}
          </span>
        </div>

        <div className="additional-loan-actions__buttons">
          <button
            type="button"
            className="additional-loan-btn additional-loan-btn--secondary"
            onClick={handleBack}
            disabled={isSubmitting}
          >
            {t("actions.cancel", "Cancel")}
          </button>

          <button
            type="button"
            className="additional-loan-btn additional-loan-btn--primary"
            onClick={() => {
              if (validate()) setShowConfirm(true);
            }}
            disabled={isSubmitting || amount <= 0}
          >
            <CheckCircle2 size={16} />
            {t("actions.create", "Record additional loan")}
          </button>
        </div>
      </div>

      {showConfirm ? (
        <div
          className="additional-loan-modal__backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isSubmitting) {
              setShowConfirm(false);
            }
          }}
        >
          <div
            className="additional-loan-modal"
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              className="additional-loan-modal__close"
              onClick={() => setShowConfirm(false)}
              disabled={isSubmitting}
            >
              <X size={18} />
            </button>

            <div className="additional-loan-modal__icon">
              <Landmark size={24} />
            </div>

            <h2>{t("confirm.title", "Record additional renovation loan?")}</h2>

            <p>
              {t(
                "confirm.text",
                "A separate additional loan case will be created and linked to the original approved case and renovation project.",
              )}
            </p>

            <div className="additional-loan-modal__amount">
              <span>{t("confirm.amount", "Additional loan")}</span>
              <strong>{formatEuro(amount)}</strong>
            </div>

            <div className="additional-loan-modal__actions">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                disabled={isSubmitting}
              >
                {t("actions.cancel", "Cancel")}
              </button>

              <button
                type="button"
                className="additional-loan-modal__confirm"
                onClick={submitAdditionalLoan}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <LoaderCircle
                    size={16}
                    className="additional-loan-page__spinner"
                  />
                ) : (
                  <CheckCircle2 size={16} />
                )}

                {isSubmitting
                  ? t("actions.creating", "Recording...")
                  : t("actions.confirm", "Confirm additional loan")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
